/**
 * Global Upload Store
 *
 * Manages UploadQueue instances globally so that file uploads survive
 * route changes.  Queue instances live at module level (outside Zustand)
 * while the reactive state (progress, status) lives inside the store.
 */

import i18n from '@/locales/config';
import {
  getFileValidationFailureSummary,
  showFileValidationFailures,
} from '@/utils/file-validation';
import { UploadQueue, UploadQueueState } from '@/utils/upload-queue';
import { QueryClient } from '@tanstack/react-query';
import { UploadFile, message } from 'antd';
import { create } from 'zustand';

/* ------------------------------------------------------------------ */
/*  Module-level queue map (NOT reactive – just keeps instances alive) */
/* ------------------------------------------------------------------ */
const uploadQueues = new Map<string, UploadQueue>();
const rejectionModalShown = new Set<string>();
const uploadSuccessCallbacks = new Map<string, () => void>();

/** When initQueue is called, we record the created entryId for the next startUpload. */
const pendingStartByKb = new Map<string, string>();

/* ------------------------------------------------------------------ */
/*  Store types                                                        */
/* ------------------------------------------------------------------ */

/** Extra metadata stored alongside each upload entry. */
export interface UploadEntryMeta {
  kbName: string;
  kbId: string;
  createdAt: number; // unix-ms timestamp
}

interface UploadStoreState {
  /** Reactive queue states keyed by entryId (kbId + '-' + timestamp) */
  queueStates: Record<string, UploadQueueState>;
  /** Metadata per entry (KB name, creation time, …) */
  entryMeta: Record<string, UploadEntryMeta>;
  /** Whether the global progress panel is expanded */
  progressPanelVisible: boolean;
}

interface UploadStoreActions {
  /**
   * Create a new UploadQueue for this batch (never replace existing).
   * entryId = kbId + '-' + timestamp, so same kb can have multiple batches.
   */
  initQueue: (
    kbId: string,
    files: UploadFile[],
    options?: {
      concurrentLimit?: number;
      parse_on_creation?: boolean;
      meta_fields?: any;
      kbName?: string;
    },
  ) => void;

  /**
   * Fire-and-forget: start the upload for the batch just created by initQueue.
   */
  startUpload: (
    kbId: string,
    queryClient: QueryClient,
    onUploadSuccess?: () => void,
  ) => void;

  cancelAll: (entryId: string) => void;
  cancelFile: (entryId: string, fileId: string) => void;
  retryFailed: (entryId: string, queryClient: QueryClient) => void;
  removeFile: (entryId: string, fileId: string) => void;

  /** Remove the queue and its state completely. */
  resetQueue: (entryId: string) => void;

  /** Cancel all queues for a knowledge base (e.g. when user cancels modal). */
  cancelAllByKb: (kbId: string) => void;

  /** Cancel file by kbId (finds entryId automatically). */
  cancelFileByKb: (kbId: string, fileId: string) => void;
  /** Retry failed by kbId (retries all batches for this kb). */
  retryFailedByKb: (kbId: string, queryClient: QueryClient) => void;
  /** Remove file by kbId (finds entryId automatically). */
  removeFileByKb: (kbId: string, fileId: string) => void;
  /** Reset all queues for a knowledge base. */
  resetByKb: (kbId: string) => void;

  /** Convenience: clear finished entries. */
  clearFinished: () => void;

  setProgressPanelVisible: (v: boolean) => void;
}

export type UploadStore = UploadStoreState & UploadStoreActions;

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
/** Build a progress-update handler that writes into the store. */
const makeProgressHandler =
  (
    kbId: string,
    set: (fn: (s: UploadStoreState) => Partial<UploadStoreState>) => void,
  ) =>
  (state: UploadQueueState) => {
    set((prev) => ({
      queueStates: { ...prev.queueStates, [kbId]: { ...state } },
    }));
  };

const notifyUploadSuccess = (entryId: string, state: UploadQueueState) => {
  if (state.successCount === 0) return;

  const callback = uploadSuccessCallbacks.get(entryId);
  if (!callback) return;
  uploadSuccessCallbacks.delete(entryId);
  try {
    callback();
  } catch (error) {
    console.error('[UploadStore] post-upload callback failed:', error);
  }
};

/* ------------------------------------------------------------------ */
/*  Store                                                              */
/* ------------------------------------------------------------------ */
export const useUploadStore = create<UploadStore>((set, get) => ({
  queueStates: {},
  entryMeta: {},
  progressPanelVisible: false,

  /* ---------- initQueue ---------- */
  initQueue: (kbId, files, options) => {
    const entryId = `${kbId}-${Date.now()}`;

    const queue = new UploadQueue(kbId, {
      concurrentLimit: options?.concurrentLimit ?? 3,
      parse_on_creation: options?.parse_on_creation ?? false,
      meta_fields: options?.meta_fields,
      onProgress: makeProgressHandler(entryId, set),
    });

    queue.addTasks(files);
    uploadQueues.set(entryId, queue);
    pendingStartByKb.set(kbId, entryId);

    const createdAt = Date.now();
    set((prev) => ({
      queueStates: { ...prev.queueStates, [entryId]: queue.getState() },
      entryMeta: {
        ...prev.entryMeta,
        [entryId]: {
          kbName: options?.kbName || kbId,
          kbId,
          createdAt,
        },
      },
      progressPanelVisible: true,
    }));
  },

  /* ---------- startUpload ---------- */
  startUpload: (kbId, queryClient, onUploadSuccess) => {
    const entryId = pendingStartByKb.get(kbId);
    if (!entryId) return;
    pendingStartByKb.delete(kbId);

    const queue = uploadQueues.get(entryId);
    if (!queue) return;
    if (onUploadSuccess) {
      uploadSuccessCallbacks.set(entryId, onUploadSuccess);
    }

    queue
      .start()
      .then(async () => {
        const state = queue.getState();

        if (state.successCount > 0) {
          await new Promise((resolve) => {
            setTimeout(resolve, 500);
          });
          notifyUploadSuccess(entryId, state);
        }

        queryClient.invalidateQueries({ queryKey: ['fetchDocumentList'] });

        const rejectedTasks = state.tasks.filter(
          (task) => task.status === 'rejected',
        );
        const parseEnqueueFailedTasks = state.tasks.filter(
          (task) => task.parseRequested && task.parseEnqueued === false,
        );
        const parseEnqueueFailedCount = parseEnqueueFailedTasks.length;
        const hasOnlyFileParseFailures =
          parseEnqueueFailedCount > 0 &&
          parseEnqueueFailedTasks.every((task) =>
            task.causeCode?.startsWith('FILE_'),
          );
        const hasOnlyEncryptedParseFailures =
          parseEnqueueFailedCount > 0 &&
          parseEnqueueFailedTasks.every(
            (task) => task.causeCode === 'FILE_ENCRYPTED',
          );
        const parseEnqueueUncertainCount = state.tasks.filter(
          (task) => task.parseRequested && task.parseEnqueued === null,
        ).length;
        if (rejectedTasks.length > 0 && !rejectionModalShown.has(entryId)) {
          rejectionModalShown.add(entryId);
          showFileValidationFailures(
            rejectedTasks.map((task) => ({
              name: task.file.name,
              error: task.error,
              error_code: task.errorCode,
              cause_code: task.causeCode,
              message_key: task.messageKey,
              declared_format: task.declaredFormat,
              detected_format: task.detectedFormat,
            })),
          );
        }

        if (parseEnqueueFailedCount > 0 && !hasOnlyEncryptedParseFailures) {
          message.warning(
            hasOnlyFileParseFailures
              ? i18n.t('message.parseEnqueueFileFailed', {
                  count: parseEnqueueFailedCount,
                  reason: getFileValidationFailureSummary(
                    parseEnqueueFailedTasks.map((task) => ({
                      error: task.error,
                      error_code: task.errorCode,
                      cause_code: task.causeCode,
                    })),
                  ),
                  defaultValue:
                    '{{count}} file(s) uploaded, but automatic parsing could not start: {{reason}}',
                })
              : i18n.t('message.parseEnqueueFailed', {
                  count: parseEnqueueFailedCount,
                  defaultValue:
                    '{{count}} file(s) uploaded, but automatic parsing failed to queue. Re-parse those files individually.',
                }),
          );
        }

        if (parseEnqueueUncertainCount > 0) {
          message.warning(
            i18n.t('message.parseEnqueueUncertain', {
              count: parseEnqueueUncertainCount,
              defaultValue:
                '{{count}} file(s) uploaded with an uncertain automatic parsing state. Refresh before retrying.',
            }),
          );
        }

        const blockingParseEnqueueFailedCount = hasOnlyEncryptedParseFailures
          ? 0
          : parseEnqueueFailedCount;

        if (state.failedCount === 0) {
          if (state.skippedCount > 0) {
            message.warning(
              i18n.t('message.duplicatesFiltered', {
                count: state.skippedCount,
                defaultValue: '{{count}} duplicate file(s) filtered',
              }),
            );
          } else if (
            blockingParseEnqueueFailedCount === 0 &&
            parseEnqueueUncertainCount === 0
          ) {
            message.success(i18n.t('message.uploaded'));
          }
        } else if (state.successCount > 0) {
          message.warning(
            state.skippedCount > 0
              ? i18n.t('message.uploadPartialFailedWithDuplicates', {
                  successCount: state.successCount,
                  failedCount: state.failedCount,
                  skippedCount: state.skippedCount,
                  defaultValue:
                    '{{successCount}} file(s) uploaded, {{failedCount}} failed, {{skippedCount}} duplicate file(s) filtered',
                })
              : i18n.t('message.uploadPartialFailed', {
                  successCount: state.successCount,
                  failedCount: state.failedCount,
                  defaultValue:
                    '{{successCount}} file(s) uploaded, {{failedCount}} failed',
                }),
          );
        } else if (state.skippedCount > 0) {
          message.warning(
            i18n.t('message.duplicatesFilteredWithFailures', {
              count: state.skippedCount,
              failedCount: state.failedCount,
              defaultValue:
                '{{count}} duplicate file(s) filtered, {{failedCount}} failed',
            }),
          );
        } else {
          message.error(i18n.t('message.uploadFailed'));
        }

        if (uploadQueues.get(entryId) === queue) {
          set((prev) => ({
            queueStates: { ...prev.queueStates, [entryId]: queue.getState() },
          }));
        }
      })
      .catch((err) => {
        console.error('[UploadStore] startUpload error:', err);
        message.error(i18n.t('message.uploadFailed'));
      });
  },

  /* ---------- cancelAll ---------- */
  cancelAll: (entryId) => {
    uploadQueues.get(entryId)?.cancelAll();
  },

  /* ---------- cancelAllByKb ---------- */
  cancelAllByKb: (kbId) => {
    for (const [eid, queue] of uploadQueues) {
      if (eid.startsWith(`${kbId}-`)) queue.cancelAll();
    }
  },

  /* ---------- cancelFileByKb ---------- */
  cancelFileByKb: (kbId, fileId) => {
    const { queueStates } = get();
    const entryId = Object.keys(queueStates).find(
      (eid) =>
        eid.startsWith(`${kbId}-`) &&
        queueStates[eid].tasks.some((t) => t.fileId === fileId),
    );
    if (entryId) uploadQueues.get(entryId)?.cancelFile(fileId);
  },

  /* ---------- retryFailedByKb ---------- */
  retryFailedByKb: (kbId, queryClient) => {
    for (const [eid, queue] of uploadQueues) {
      if (eid.startsWith(`${kbId}-`)) {
        queue
          .retryFailed()
          .then(() => {
            notifyUploadSuccess(eid, queue.getState());
            queryClient.invalidateQueries({ queryKey: ['fetchDocumentList'] });
          })
          .catch(console.error);
      }
    }
  },

  /* ---------- removeFileByKb ---------- */
  removeFileByKb: (kbId, fileId) => {
    const { queueStates } = get();
    const entryId = Object.keys(queueStates).find(
      (eid) =>
        eid.startsWith(`${kbId}-`) &&
        queueStates[eid].tasks.some((t) => t.fileId === fileId),
    );
    if (entryId) uploadQueues.get(entryId)?.removeFile(fileId);
  },

  /* ---------- resetByKb ---------- */
  resetByKb: (kbId) => {
    const { queueStates } = get();
    Object.keys(queueStates)
      .filter((eid) => eid.startsWith(`${kbId}-`))
      .forEach((eid) => get().resetQueue(eid));
  },

  /* ---------- cancelFile ---------- */
  cancelFile: (entryId, fileId) => {
    uploadQueues.get(entryId)?.cancelFile(fileId);
  },

  /* ---------- retryFailed ---------- */
  retryFailed: (entryId, queryClient) => {
    const queue = uploadQueues.get(entryId);
    if (!queue) return;

    queue
      .retryFailed()
      .then(() => {
        notifyUploadSuccess(entryId, queue.getState());
        queryClient.invalidateQueries({ queryKey: ['fetchDocumentList'] });
      })
      .catch(console.error);
  },

  /* ---------- removeFile ---------- */
  removeFile: (entryId, fileId) => {
    uploadQueues.get(entryId)?.removeFile(fileId);
  },

  /* ---------- resetQueue ---------- */
  resetQueue: (entryId) => {
    const queue = uploadQueues.get(entryId);
    if (queue) {
      queue.cancelAll();
      uploadQueues.delete(entryId);
      rejectionModalShown.delete(entryId);
      uploadSuccessCallbacks.delete(entryId);
    }
    set((prev) => {
      const nextStates = { ...prev.queueStates };
      const nextMeta = { ...prev.entryMeta };
      delete nextStates[entryId];
      delete nextMeta[entryId];
      return { queueStates: nextStates, entryMeta: nextMeta };
    });
  },

  /* ---------- clearFinished ---------- */
  clearFinished: () => {
    const { queueStates, entryMeta } = get();
    const nextStates: Record<string, UploadQueueState> = {};
    const nextMeta: Record<string, UploadEntryMeta> = {};
    for (const [entryId, s] of Object.entries(queueStates)) {
      if (s.isUploading) {
        nextStates[entryId] = s;
        if (entryMeta[entryId]) nextMeta[entryId] = entryMeta[entryId];
      } else {
        uploadQueues.delete(entryId);
        rejectionModalShown.delete(entryId);
        uploadSuccessCallbacks.delete(entryId);
      }
    }
    set({ queueStates: nextStates, entryMeta: nextMeta });
  },

  /* ---------- setProgressPanelVisible ---------- */
  setProgressPanelVisible: (v) => set({ progressPanelVisible: v }),
}));

/* ------------------------------------------------------------------ */
/*  Derived selectors (for convenience)                                */
/* ------------------------------------------------------------------ */
/** True when at least one knowledge base has an active upload. */
export const selectHasActiveUploads = (s: UploadStore) =>
  Object.values(s.queueStates).some((q) => q.isUploading);

/** Total number of entries (active + finished-with-errors). */
export const selectEntryCount = (s: UploadStore) =>
  Object.keys(s.queueStates).length;
